import { keyframes } from "@mui/material/styles";

// Look and motion of the sign in page. Motion is switched off for visitors who ask for less of it.

export const floatA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(60px, 40px) scale(1.12); }
`;
export const floatB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-70px, -50px) scale(1.1); }
`;
export const drift = keyframes`
  0% { transform: translateY(0) scale(0.6); opacity: 0; }
  15% { opacity: 0.9; }
  100% { transform: translateY(-105vh) scale(1.1); opacity: 0; }
`;
export const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
export const riseIn = keyframes`
  from { opacity: 0; transform: translateY(32px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;
export const slideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); filter: blur(3px); }
  to { opacity: 1; transform: translateY(0); filter: blur(0); }
`;
export const growLine = keyframes`
  from { width: 0; opacity: 0; }
  to { width: 44px; opacity: 1; }
`;
export const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
`;
export const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
`;
export const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// children appear one after the other
export const stagger = (delay = 0) => ({
  animation: `${fadeUp} 0.6s ${delay}s both cubic-bezier(0.22, 1, 0.36, 1)`,
});

export const pageSx = {
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  px: 2,
  py: 3,
  background: "linear-gradient(120deg, #17110a, #2c200d, #4a3512, #2c200d, #17110a)",
  backgroundSize: "300% 300%",
  animation: `${gradientShift} 22s ease infinite`,
  "@media (prefers-reduced-motion: reduce)": { "&, & *": { animation: "none !important", transition: "none !important" } },
};

export const orbSx = { position: "absolute", borderRadius: "50%", filter: "blur(40px)", pointerEvents: "none" };

export const sparkSx = {
  position: "absolute",
  bottom: "-20px",
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "radial-gradient(circle, #f4e9c9, rgba(200,160,84,0))",
  pointerEvents: "none",
  animation: `${drift} 9s linear infinite`,
};

export const cardSx = {
  position: "relative",
  zIndex: 1,
  width: "100%",
  maxWidth: 380,
  bgcolor: "rgba(255,255,255,0.97)",
  backdropFilter: "blur(14px)",
  borderRadius: "20px",
  px: { xs: 2.5, sm: 3.5 },
  py: { xs: 2.75, sm: 3.25 },
  boxShadow: "0 30px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(200,160,84,0.25)",
  animation: `${riseIn} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both`,
};

export const badgeSx = {
  display: "inline-block",
  px: 1.6,
  py: 0.4,
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
};

// Soft inputs: a warm tinted fill, a hairline edge that fades in, and a gentle glow on focus (no hard outline)
export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "#faf6ec",
    fontSize: "0.92rem",
    transition: "background-color 0.35s ease, box-shadow 0.35s ease",
    "& fieldset": { borderColor: "rgba(200,160,84,0.22)", borderWidth: 1, transition: "border-color 0.35s ease" },
    "&:hover": { bgcolor: "#f7f1e2" },
    "&:hover fieldset": { borderColor: "rgba(200,160,84,0.45)" },
    "&.Mui-focused": { bgcolor: "#fff", boxShadow: "0 0 0 4px rgba(200,160,84,0.14), 0 6px 18px rgba(200,160,84,0.12)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(200,160,84,0.7)", borderWidth: 1 },
    "&.Mui-error fieldset": { borderColor: "rgba(211,47,47,0.55)" },
    "&.Mui-error.Mui-focused": { boxShadow: "0 0 0 4px rgba(211,47,47,0.10)" },
  },
  // hints live inside the box as placeholders (no floating labels)
  "& .MuiInputBase-input": { "&::placeholder": { color: "#8f8566", opacity: 0.85 } },
  "& .MuiSelect-select .placeholder-text": { color: "#8f8566", opacity: 0.85 },
  "& .MuiFormHelperText-root": { marginLeft: 1.5, marginTop: "2px", fontSize: "0.7rem", lineHeight: 1.2, minHeight: 14 },
};

export const goldButtonSx = {
  py: 1.05,
  borderRadius: "10px",
  textTransform: "none",
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#fff",
  backgroundImage: "linear-gradient(110deg, #b38841 0%, #C8A054 35%, #e6c987 50%, #C8A054 65%, #b38841 100%)",
  backgroundSize: "200% 100%",
  boxShadow: "0 8px 20px rgba(200,160,84,0.4)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": { backgroundPosition: "100% 0", transform: "translateY(-2px)", boxShadow: "0 12px 26px rgba(200,160,84,0.5)", color: "#fff" },
  "&:active": { transform: "translateY(0)" },
  "&:disabled": { backgroundImage: "none", backgroundColor: "#ccc", color: "#fff", boxShadow: "none" },
};

export const toggleGroupSx = {
  mb: 1,
  p: 0.5,
  bgcolor: "#f6f1e4",
  borderRadius: "14px",
  "& .MuiToggleButton-root": {
    border: 0,
    borderRadius: "10px !important",
    textTransform: "none",
    fontWeight: 600,
    py: 0.7,
    fontSize: "0.85rem",
    gap: 0.75,
    color: "#7a6a45",
    transition: "all 0.25s ease",
  },
  "& .Mui-selected": {
    bgcolor: "#fff !important",
    color: "#8a6a24 !important",
    boxShadow: "0 3px 10px rgba(138,106,36,0.22)",
  },
};
